import { useEffect, useId, useRef, useState } from 'react'
import { messageOf } from '../lib/errors'
import { useDismiss } from '../lib/useDismiss'
import { PickerFoot } from './PickerFoot'
import { useHubLang } from '../lib/i18n'
import { STARS, TERMS } from '../lib/hubCopy'
import { useStarOne } from '../lib/terms'
import {
  clampGift, findPeople, giveRecognition, STAR_PRESETS,
  type Endorsement, type PersonOption,
} from '../lib/stars'
import { EndorsementField } from './EndorsementField'

/**
 * The give form. REC-3, with REC-2's picker and REC-17's custom amount.
 *
 * ── IT LIVES HERE AND NOT IN `routes/Stars.tsx`, FOR A MEASURED REASON ──────
 *
 * 062 put `RecognitionCard` in `components/` because importing a route pulls in
 * `AppShell` and then an SVG the test bundler cannot resolve — so a component
 * defined in a route file is one the suite physically cannot render. That is
 * exactly how the first run of the wall showed 357 passing assertions and zero
 * coverage of the new screen. This form carries the allowance cap, which is the
 * one piece of client-side arithmetic on this screen worth guarding, so it was
 * never going to be safe to leave where nothing could mount it.
 *
 * ── THE ORDER IS WHO -> WHAT FOR -> WORDS -> STARS, AND STARS ARE LAST ──────
 *
 * From the mockup, and worth keeping: putting the amount first makes it a
 * transaction, putting it last makes it an emphasis on something you have
 * already said. "None" is a first-class preset rather than an absence — a
 * shout-out is a recognition with zero stars, one row and the same wall (061).
 */
export function GiveForm({
  featured,
  allowanceRemaining,
  onGiven,
  onDone,
}: {
  /** The preset chips — featured official endorsements (128). */
  featured: Endorsement[]
  allowanceRemaining: number
  onGiven: () => Promise<void>
  /** Called after a successful give, so a host dialog can close itself. */
  onDone?: () => void
}) {
  const lang = useHubLang()
  const t = STARS[lang]
  const terms = TERMS[lang]
  // 165. Not joined Star One → shout-outs only (amount stays 0).
  const joined = useStarOne().status === 'agreed'
  const [person, setPerson] = useState<PersonOption | null>(null)
  // 128: what it is for is one to three WORDS, picked or typed.
  const [endorsements, setEndorsements] = useState<string[]>([])
  const [body, setBody] = useState('')
  const [amount, setAmount] = useState(0)
  const [custom, setCustom] = useState(false)
  const [customText, setCustomText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const customRef = useRef<HTMLInputElement>(null)

  const overAllowance = amount > allowanceRemaining
  const ready = Boolean(person && endorsements.length > 0 && body.trim() && !overAllowance)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!ready || !person || busy) return
    setBusy(true)
    setError(null)
    try {
      await giveRecognition({
        personId: person.personId,
        endorsements,
        body: body.trim(),
        stars: amount,
      })
      setDone(person.displayName)
      setPerson(null)
      setEndorsements([])
      setBody('')
      setAmount(0)
      setCustom(false)
      setCustomText('')
      await onGiven()
      onDone?.()
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="stars__give" onSubmit={submit}>
      <PersonField person={person} onPick={setPerson} />

      <EndorsementField featured={featured} value={endorsements} onChange={setEndorsements} />

      <label className="stars__field">
        <span className="composer__label">{t.inYourWords}</span>
        <textarea
          className="composer__body"
          rows={3}
          maxLength={2000}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={t.bodyPlaceholder}
        />
      </label>

      {/* 165. Not joined Star One: no stars to add (the database refuses
          them too), but a shout-out is not giving stars and still goes. */}
      {!joined ? (
        <p className="staragree__givenote">
          {terms.giveNeedsStarOne} <a href="#/stars">{terms.goToStarOne}</a>
        </p>
      ) : (
      /* Stars LAST and optional. "None" is a preset, not the absence of one. */
      <fieldset className="stars__field">
        <legend className="composer__label">
          {t.addStars} <span className="composer__optional">{t.optional}</span>
        </legend>
        <div className="chips">
          {STAR_PRESETS.map((n) => {
            const on = !custom && amount === n
            return (
              <button
                key={n}
                type="button"
                className={`chip${on ? ' chip--on' : ''}`}
                aria-pressed={on}
                onClick={() => {
                  setCustom(false)
                  setAmount(n)
                }}
              >
                {n === 0 ? t.none : `★ ${n}`}
              </button>
            )
          })}
          {/* REC-17. "Other" is the ESCAPE HATCH, not the default, and it is
              deliberately NOT a sixth entry in STAR_PRESETS: the presets teach
              the norm, because an empty box makes 933 people guess and the
              guesses will not agree. Adding it to the array would make the
              free-text box look like one more suggested amount. */}
          <button
            type="button"
            className={`chip${custom ? ' chip--on' : ''}`}
            aria-pressed={custom}
            onClick={() => {
              const next = !custom
              setCustom(next)
              if (next) {
                setAmount(clampGift(customText, allowanceRemaining))
                // The box is useless until it has the caret; opening it and
                // making somebody aim at it is a click the chip already earned.
                requestAnimationFrame(() => customRef.current?.focus())
              } else {
                setAmount(0)
              }
            }}
          >
            {t.other}
          </button>
        </div>

        {custom && (
          <label className="stars__custom">
            <span className="composer__label">{t.howMany}</span>
            <input
              ref={customRef}
              type="number"
              inputMode="numeric"
              min={0}
              // The cap is what is left to GIVE this month, never the balance —
              // see clampGift. `max` is a hint to the browser's spinner; the
              // clamp below is what actually holds, and star_period's CHECK is
              // what holds if neither runs.
              max={allowanceRemaining}
              step={1}
              value={customText}
              onChange={(e) => {
                setCustomText(e.target.value)
                setAmount(clampGift(e.target.value, allowanceRemaining))
              }}
              placeholder="0"
            />
            <span className="stars__cap">
              {t.upTo(allowanceRemaining.toLocaleString())}
            </span>
          </label>
        )}

        {overAllowance && (
          <p className="composer__error" role="alert">
            {t.overAllowance(allowanceRemaining.toLocaleString())}
          </p>
        )}
      </fieldset>
      )}

      {error && <p className="composer__error" role="alert">{error}</p>}
      {done && (
        <p className="stars__done" role="status">
          {t.thanks(done)}
        </p>
      )}

      <div className="composer__actions">
        <button className="btn btn--primary" type="submit" disabled={!ready || busy}>
          {busy ? t.sending : t.give}
        </button>
        <p className="composer__note">{t.withYourName}</p>
      </div>
    </form>
  )
}

/**
 * The Who field. REC-2.
 *
 * It reuses the app's ONE definition of who is findable — `find_colleagues`,
 * which shares its body with the @ menu's `mentionable_people` (059, 335) and
 * matches every typed word against the name, unit or title each row shows —
 * and the picker's own markup classes, so this is not a second person search
 * and not a second visual language. Nothing is listed until something is
 * typed (335). It is NOT the `Picker`
 * component: that filters a fixed `options` array in the browser, which is
 * right for 69 org units and wrong for 932 people, where the search has to
 * happen in the database.
 *
 * BOTH THE UNIT AND THE TITLE ARE RENDERED, AND THAT IS MEASURED (062c).
 * The two James Millers share a business unit and differ by title; the two
 * Kenneth Taylors share a title and differ by unit. Showing one field would
 * leave half the real duplicates as two identical rows, and the giver picks
 * one at random — the failure the @ token was built to prevent, one layer up.
 */
export function PersonField({
  person,
  onPick,
  label,
}: {
  person: PersonOption | null
  onPick: (p: PersonOption | null) => void
  /** 119: the support desk reuses this field to hand a question to a
   *  colleague — the ONE person search (`mentionable_people`), not a copy of
   *  it. Its label is "Who?" unless the caller says otherwise. */
  label?: string
}) {
  const lang = useHubLang()
  const t = STARS[lang]
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [people, setPeople] = useState<PersonOption[]>([])
  const [active, setActive] = useState(0)
  // What MATCHED, from the server (088). Counting `people` would always equal
  // the page size and say nothing — REC-54 is exactly that mistake on screen.
  const [total, setTotal] = useState(0)

  const wrapRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listId = useId()

  // Escape returns focus to the field; a click elsewhere does not, because the
  // click already said where the person wants to be. useDismiss's own rule.
  //
  // Inside the give dialog this is the SURFACE ABOVE, so Escape closes the list
  // and leaves the form standing — the stack in useDismiss is what makes that
  // true, and without it one press would discard a half-written message.
  useDismiss(wrapRef, open, (viaKeyboard) => {
    setOpen(false)
    if (viaKeyboard) inputRef.current?.focus()
  })

  // 335 (David): nothing is listed until something is typed. An empty box is
  // not a question, and 25 alphabetical strangers is not an answer to one.
  const typed = query.trim() !== ''
  const showing = open && typed

  useEffect(() => {
    if (!showing) {
      setPeople([])
      setTotal(0)
      return
    }
    let live = true
    const t = setTimeout(() => {
      // REC-53: not you. `give_recognition()` refuses self-recognition, so a
      // list that offers your own name can only teach the rule by raising an
      // exception at you — and an exception reads as the app being broken.
      // The filter is the SERVER'S, because the limit is (087, rule 1).
      findPeople(query, { excludeSelf: true })
        .then((page) => {
          if (!live) return
          setPeople(page.people)
          // REC-54: how many MATCHED, not how many arrived. Sixteen people
          // match "david" and eight come back; without this the list looks
          // complete and reads as a broken scroll.
          setTotal(page.total)
          setActive(0)
        })
        .catch(() => {
          if (!live) return
          setPeople([])
          setTotal(0)
        })
    }, 150)
    return () => {
      live = false
      clearTimeout(t)
    }
  }, [query, showing])

  if (person) {
    return (
      <div className="stars__field">
        <span className="composer__label">{label ?? t.who}</span>
        <div className="chips">
          <span className="chip chip--value">
            {person.displayName}
            {personDetail(person) && (
              <span className="chip__of"> · {personDetail(person)}</span>
            )}
            <button
              type="button"
              className="chip__x"
              aria-label={t.removePerson(person.displayName)}
              onClick={() => onPick(null)}
            >
              ×
            </button>
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="stars__field" ref={wrapRef}>
      <label className="composer__label" htmlFor={`${listId}-input`}>{label ?? t.who}</label>
      <input
        id={`${listId}-input`}
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={showing}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={showing && people.length ? `${listId}-${active}` : undefined}
        placeholder={t.searchColleague}
        value={query}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
        }}
        onKeyDown={(e) => {
          if (!showing || people.length === 0) return
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setActive((i) => (i + 1) % people.length)
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActive((i) => (i - 1 + people.length) % people.length)
          } else if (e.key === 'Enter') {
            e.preventDefault()
            onPick(people[active])
            setOpen(false)
            setQuery('')
          }
        }}
      />
      {showing && (
        <div className="picker__panel picker__panel--people">
          {people.length === 0 ? (
            <p className="picker__none">{t.noOneMatches}</p>
          ) : (
            <ul className="picker__list" id={listId} role="listbox">
              {people.map((p, i) => (
                <li
                  key={p.personId}
                  id={`${listId}-${i}`}
                  role="option"
                  aria-selected={i === active}
                  data-active={i === active}
                  className="picker__opt picker__opt--person"
                  // onMouseDown, not onClick: the input's blur fires first on a
                  // click and would close the panel before the choice lands.
                  onMouseDown={(e) => {
                    e.preventDefault()
                    onPick(p)
                    setOpen(false)
                    setQuery('')
                  }}
                >
                  {/* 335: three fixed columns — name, unit, title — each on one
                      line, cut with an ellipsis and whole on hover. The name
                      column fits ~97% of the roster (p95 22 characters). */}
                  <span className="picker__personname" title={p.displayName}>{p.displayName}</span>
                  <span className="picker__personcol" title={p.businessUnit ?? undefined}>{p.businessUnit ?? ''}</span>
                  <span className="picker__personcol" title={p.jobTitle ?? undefined}>{p.jobTitle ?? ''}</span>
                </li>
              ))}
            </ul>
          )}
          {/* REC-54. Outside the `people.length === 0` branch on purpose: when
              nothing matched there is no page to describe, and "No one by that
              name" is already the whole truth. */}
          <PickerFoot shown={people.length} total={total} hasQuery={Boolean(query.trim())} />
        </div>
      )}
    </div>
  )
}

/**
 * The line under a name in the picker. Unit and title, whichever exist —
 * see 062c for why it must not be reduced to one of them.
 */
function personDetail(p: PersonOption): string {
  return [p.businessUnit, p.jobTitle].filter(Boolean).join(' · ')
}
